import { useState, useRef } from 'react';
import { compressImageLossless, formatBytes, getCompressionStats } from '../utils/imageCompression';
import { loadCompressionSettings } from '../utils/compressionSettings';
import type { CompressionResult } from '../types/imageCompression';

interface ImageUploaderProps {
  onImagesProcessed: (dataUrls: string[]) => void;
  maxImages?: number;
}

export default function ImageUploader({ onImagesProcessed, maxImages = 10 }: ImageUploaderProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<CompressionResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (files.length > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setResults([]);

    const config = loadCompressionSettings();
    const processedResults: CompressionResult[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const result = await compressImageLossless(file, config);
        processedResults.push(result);
        setProgress(((i + 1) / files.length) * 100);
      } catch (error) {
        console.error('Failed to process image:', error);
      }
    }

    setResults(processedResults);
    setIsProcessing(false);
    setShowResults(true);

    // Extract data URLs
    const dataUrls = processedResults.map((r) => r.dataUrl);
    onImagesProcessed(dataUrls);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));

    if (files.length > 0) {
      const input = fileInputRef.current;
      if (input) {
        const dataTransfer = new DataTransfer();
        files.forEach((file) => dataTransfer.items.add(file));
        input.files = dataTransfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const stats = results.length > 0 ? getCompressionStats(results) : null;

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="image-upload"
        />
        <label htmlFor="image-upload" className="cursor-pointer">
          <div className="text-gray-600">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-2 text-sm font-medium">Click to upload or drag and drop</p>
            <p className="mt-1 text-xs text-gray-500">
              PNG, JPG, GIF, WebP up to 10MB (max {maxImages} images)
            </p>
          </div>
        </label>
      </div>

      {/* Processing Progress */}
      {isProcessing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Processing images...</span>
            <span className="text-sm text-blue-700">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Results */}
      {showResults && stats && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-green-900 mb-2">✓ Images Processed</h3>
              <div className="text-sm text-green-800 space-y-1">
                <p>
                  <strong>Total images:</strong> {results.length}
                </p>
                <p>
                  <strong>Compressed:</strong> {stats.compressedCount}
                </p>
                <p>
                  <strong>Original size:</strong> {formatBytes(stats.totalOriginalSize)}
                </p>
                <p>
                  <strong>Final size:</strong> {formatBytes(stats.totalCompressedSize)}
                </p>
                <p>
                  <strong>Saved:</strong> {formatBytes(stats.totalSaved)} (
                  {((1 - stats.averageRatio) * 100).toFixed(1)}%)
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowResults(false)}
              className="text-green-600 hover:text-green-700"
            >
              ×
            </button>
          </div>

          {/* Individual Results */}
          <details className="mt-4">
            <summary className="text-sm font-medium text-green-900 cursor-pointer">
              View Details
            </summary>
            <div className="mt-2 space-y-2">
              {results.map((result, index) => (
                <div key={index} className="text-xs text-green-700 bg-white p-2 rounded">
                  <p>
                    <strong>Image {index + 1}:</strong> {formatBytes(result.originalSize)} →{' '}
                    {formatBytes(result.compressedSize)} ({result.format})
                  </p>
                  {result.wasCompressed && (
                    <p className="text-green-600">
                      ✓ Compressed ({((1 - result.compressionRatio) * 100).toFixed(1)}% saved)
                    </p>
                  )}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
